/**
 * Invoice utility functions for status management and business logic
 */

/**
 * Check if an invoice can be edited
 * @param {Object} invoice - Invoice object
 * @returns {boolean} - True if invoice can be edited
 */
export const canEditInvoice = (invoice) => {
  // Allow editing for Pending and Error status invoices
  return invoice && (invoice.status === 'Pending' || invoice.status === 'Error');
};

/**
 * Check if an invoice can be deleted
 * @param {Object} invoice - Invoice object
 * @returns {boolean} - True if invoice can be deleted
 */
export const canDeleteInvoice = (invoice) => {
  // Allow deleting for any status except Submitted
  return invoice && invoice.status !== 'Submitted';
};

/**
 * Check if an invoice can be validated
 * @param {Object} invoice - Invoice object
 * @returns {boolean} - True if invoice can be validated
 */
export const canValidateInvoice = (invoice) => {
  // Allow validation for Pending and Error invoices (retry)
  // Disable for Validated and Submitted invoices
  return !!invoice && (invoice.status === 'Pending' || invoice.status === 'Error');
};

/**
 * Check if an invoice can be submitted
 * @param {Object} invoice - Invoice object
 * @returns {boolean} - True if invoice can be submitted
 */
export const canSubmitInvoice = (invoice) => {
  // Allow submission for Pending (auto-validate), Error (auto-validate), and Validated invoices
  // Disable for already Submitted invoices
  return !!invoice && (['Validated', 'Pending', 'Error'].includes(invoice.status)) && invoice.status !== 'Submitted';
};

/**
 * Get button states for an invoice
 * @param {Object} invoice - Invoice object
 * @returns {Object} - Button states and configurations
 */
export const getInvoiceButtonStates = (invoice) => {
  if (!invoice) return { validate: null, submit: null };

  const states = {
    validate: {
      show: true,
      enabled: false,
      className: '',
      title: '',
      color: 'gray'
    },
    submit: {
      show: true,
      enabled: false,
      className: '',
      title: '',
      color: 'gray'
    }
  };

  switch (invoice.status) {
    case 'Pending':
      states.validate = {
        show: true,
        enabled: true,
        className: 'text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50',
        title: 'Validate Invoice',
        color: 'yellow'
      };
      states.submit = {
        show: true,
        enabled: true,
        className: 'text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50',
        title: 'Validate and Submit to FBR',
        color: 'purple'
      };
      break;

    case 'Error':
      // Enable both buttons for Error status - users can retry
      states.validate = {
        show: true,
        enabled: true,
        className: 'text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50',
        title: 'Retry Validation (Previous attempt failed)',
        color: 'red'
      };
      states.submit = {
        show: true,
        enabled: true,
        className: 'text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50',
        title: 'Retry Validation and Submit to FBR',
        color: 'red'
      };
      break;

    case 'Validated':
      states.validate = {
        show: true,
        enabled: false,
        className: 'text-gray-400 p-1 rounded cursor-not-allowed',
        title: 'Invoice already validated',
        color: 'gray'
      };
      states.submit = {
        show: true,
        enabled: true,
        className: 'text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50',
        title: 'Submit to FBR',
        color: 'purple'
      };
      break;

    case 'Submitted':
      states.validate = {
        show: true,
        enabled: false,
        className: 'text-green-400 p-1 rounded cursor-not-allowed',
        title: 'Invoice already validated and submitted',
        color: 'green'
      };
      states.submit = {
        show: true,
        enabled: false,
        className: 'text-green-400 p-1 rounded cursor-not-allowed',
        title: 'Invoice already submitted to FBR',
        color: 'green'
      };
      break;

    default:
      // For unknown statuses, show buttons but disabled
      states.validate = {
        show: true,
        enabled: false,
        className: 'text-gray-400 p-1 rounded cursor-not-allowed',
        title: `Cannot validate invoice with status: ${invoice.status}`,
        color: 'gray'
      };
      states.submit = {
        show: true,
        enabled: false,
        className: 'text-gray-400 p-1 rounded cursor-not-allowed',
        title: `Cannot submit invoice with status: ${invoice.status}`,
        color: 'gray'
      };
  }

  return states;
};

/**
 * Get status badge configuration
 * @param {string} status - Invoice status
 * @returns {Object} - Badge configuration with color and text
 */
export const getStatusBadgeConfig = (status) => {
  const configs = {
    'Pending': {
      color: 'bg-yellow-100 text-yellow-800',
      text: 'Pending',
      icon: '⏳'
    },
    'Validated': {
      color: 'bg-blue-100 text-blue-800',
      text: 'Validated',
      icon: '✓'
    },
    'Submitted': {
      color: 'bg-green-100 text-green-800',
      text: 'Submitted',
      icon: '📤'
    },
    'Error': {
      color: 'bg-red-100 text-red-800',
      text: 'Error',
      icon: '❌'
    }
  };

  return configs[status] || {
    color: 'bg-gray-100 text-gray-800',
    text: status || 'Unknown',
    icon: '❓'
  };
};

/**
 * Format invoice date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date
 */
export const formatInvoiceDate = (dateString) => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: PKR)
 * @returns {string} - Formatted currency
 */
export const formatCurrency = (amount, currency = 'PKR') => {
  if (amount === null || amount === undefined) return '-';

  try {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Format sales tax amount for display
 * Returns an object with formatted value and styling info
 * @param {number} amount - Sales tax amount to format
 * @returns {Object} - Object with formatted value and styling info
 */
export const formatSalesTax = (amount) => {
  if (amount === null || amount === undefined) {
    return {
      formatted: '-',
      hasDecimals: false,
      className: ''
    };
  }

  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) {
    return {
      formatted: '-',
      hasDecimals: false,
      className: ''
    };
  }

  // Always display with 2 decimal places
  const formatted = numAmount.toFixed(2);

  // Determine if original number had decimals (for optional styling)
  const hasDecimals = numAmount % 1 !== 0;

  return {
    formatted,
    hasDecimals,
    className: ''
  };
};

/**
 * Get available actions for an invoice based on its status
 * @param {Object} invoice - Invoice object
 * @returns {Array} - Array of available action objects
 */
export const getAvailableActions = (invoice) => {
  if (!invoice) return [];

  const actions = [];

  // View action is always available
  actions.push({
    key: 'view',
    label: 'View',
    icon: '👁️',
    variant: 'secondary'
  });

  // Edit action for pending and error invoices
  if (canEditInvoice(invoice)) {
    actions.push({
      key: 'edit',
      label: 'Edit',
      icon: '✏️',
      variant: 'primary'
    });
  }

  // Delete action for pending and error invoices
  if (canDeleteInvoice(invoice)) {
    actions.push({
      key: 'delete',
      label: 'Delete',
      icon: '🗑️',
      variant: 'danger'
    });
  }

  // Validate action for pending invoices
  if (canValidateInvoice(invoice)) {
    actions.push({
      key: 'validate',
      label: 'Validate',
      icon: '✅',
      variant: 'success'
    });
  }

  // Submit action for validated invoices
  if (canSubmitInvoice(invoice)) {
    actions.push({
      key: 'submit',
      label: 'Submit to FBR',
      icon: '📤',
      variant: 'success'
    });
  }

  return actions;
};

/**
 * Get status description for user
 * @param {string} status - Invoice status
 * @returns {string} - User-friendly status description
 */
export const getStatusDescription = (status) => {
  const descriptions = {
    'Pending': 'Invoice is pending validation and can be edited or deleted',
    'Validated': 'Invoice has been validated with FBR and is ready for submission',
    'Submitted': 'Invoice has been successfully submitted to FBR',
    'Error': 'There was an error processing this invoice'
  };

  return descriptions[status] || 'Unknown status';
};

/**
 * Sort invoices by various criteria
 * @param {Array} invoices - Array of invoices
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - 'ASC' or 'DESC'
 * @returns {Array} - Sorted invoices
 */
export const sortInvoices = (invoices, sortBy = 'created_at', sortOrder = 'DESC') => {
  if (!Array.isArray(invoices)) return [];

  return [...invoices].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle date fields
    if (sortBy.includes('date') || sortBy.includes('_at')) {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    // Handle numeric fields
    if (sortBy === 'total_amount' || sortBy === 'item_count') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    // Handle string fields
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    let comparison = 0;
    if (aValue > bValue) {
      comparison = 1;
    } else if (aValue < bValue) {
      comparison = -1;
    }

    return sortOrder === 'DESC' ? comparison * -1 : comparison;
  });
};

/**
 * Filter invoices by status
 * @param {Array} invoices - Array of invoices
 * @param {string} status - Status to filter by (null for all)
 * @returns {Array} - Filtered invoices
 */
export const filterInvoicesByStatus = (invoices, status) => {
  if (!Array.isArray(invoices)) return [];
  if (!status || status === 'all') return invoices;

  return invoices.filter(invoice => invoice.status === status);
};

/**
 * Search invoices by text
 * @param {Array} invoices - Array of invoices
 * @param {string} searchText - Text to search for
 * @returns {Array} - Filtered invoices
 */
export const searchInvoices = (invoices, searchText) => {
  if (!Array.isArray(invoices)) return [];
  if (!searchText || searchText.trim() === '') return invoices;

  const searchLower = searchText.toLowerCase().trim();

  return invoices.filter(invoice => {
    return (
      (invoice.seller_business_name && invoice.seller_business_name.toLowerCase().includes(searchLower)) ||
      (invoice.buyer_business_name && invoice.buyer_business_name.toLowerCase().includes(searchLower)) ||
      (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(searchLower)) ||
      (invoice.status && invoice.status.toLowerCase().includes(searchLower)) ||
      (invoice.invoice_type && invoice.invoice_type.toLowerCase().includes(searchLower))
    );
  });
};

/**
 * Extract FBR Invoice Number from invoice data
 * @param {Object} invoice - Invoice object
 * @returns {string} - FBR invoice number or 'Not assigned'
 */
export const getFBRInvoiceNumber = (invoice) => {
  try {
    // First try to get from fbr_invoice_number field
    if (invoice.fbr_invoice_number) {
      return invoice.fbr_invoice_number;
    }
    
    // If not available, try to extract from fbr_submission JSON
    if (invoice.fbr_submission) {
      const fbrData = typeof invoice.fbr_submission === 'string' 
        ? JSON.parse(invoice.fbr_submission) 
        : invoice.fbr_submission;
      
      // Navigate through the JSON structure to find invoiceNo
      // Structure: submission.rawResponse.validationResponse.invoiceStatuses[0].invoiceNo
      const invoiceStatuses = fbrData?.submission?.rawResponse?.validationResponse?.invoiceStatuses;
      if (invoiceStatuses && invoiceStatuses.length > 0 && invoiceStatuses[0].invoiceNo) {
        return invoiceStatuses[0].invoiceNo;
      }
    }
    
    return 'Not assigned';
  } catch (error) {
    console.error('Error parsing FBR submission data:', error);
    return 'Not assigned';
  }
};
